/**
 * PlanApprovalQueuePage - Domain-agnostic plan approval queue (DRR-232)
 *
 * Generic page for any industry's plan approval workflow:
 * - Healthcare: DoN reviews care plans (subject = resident)
 * - Finance: CFO reviews financial plans (subject = account)
 * - Legal: Partner reviews case plans (subject = client)
 * - Construction: PM reviews project plans (subject = project)
 *
 * The page uses generic queries; domain-specific rendering is handled
 * by RendererRegistry, which resolves renderers from subgraph packages.
 *
 * Access control:
 *   - RequireScope("plan:read") on backend
 *   - Role determines which queue items are visible
 */

import { useState, useCallback, Suspense } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Alert,
  Button,
  Group,
  Badge,
  Card,
  Divider,
  LoadingOverlay,
  SimpleGrid,
  ThemeIcon,
  Skeleton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconUser,
  IconClock,
  IconRefresh,
  IconChevronRight,
} from '@tabler/icons-react';
// eslint-disable-next-line no-restricted-imports -- Data layer hook requires direct Apollo access
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

import { useAuth, useViewLayer } from '@/auth';
import { useResolvedTerminology } from '@/hooks/useResolvedTerminology';
import { useRenderer, useRendererNameFromMetadata } from '@/components/RendererRegistry';

// =============================================================================
// GraphQL - Uses generic query from Track B (DRR-232)
// =============================================================================

const APPROVER_PLAN_QUEUE = gql`
  query ApproverPlanQueue($authorityRole: String!, $limit: Int) {
    approverPlanQueue(authorityRole: $authorityRole, limit: $limit) {
      planId
      subjectId
      subjectName
      status
      createdAt
      activityDomains
      reviewerNotes
      reviewedAt
      reviewedBy
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

interface PlanQueueItem {
  planId: string;
  subjectId: string;
  subjectName: string;
  status: string;
  createdAt: string;
  activityDomains: string[];
  reviewerNotes: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
}

// =============================================================================
// Status Badge
// =============================================================================

function StatusBadge({ status }: { status: string }) {
  const statusColors: Record<string, string> = {
    PENDING_REVIEW: 'yellow',
    IN_REVIEW: 'blue',
    APPROVED: 'green',
    REJECTED: 'red',
    CHANGES_REQUESTED: 'orange',
  };

  const color = statusColors[status] ?? 'gray';
  const label = status.replace(/_/g, ' ').toLowerCase();

  return (
    <Badge color={color} variant="light" style={{ textTransform: 'capitalize' }}>
      {label}
    </Badge>
  );
}

// =============================================================================
// Queue Item Card (Generic)
// =============================================================================

function QueueItemCard({
  item,
  onSelect,
  selected,
  subjectLabel,
}: {
  item: PlanQueueItem;
  onSelect: () => void;
  selected: boolean;
  subjectLabel: string;
}) {
  const createdDate = new Date(item.createdAt);
  const waitingDays = Math.floor(
    (Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <Card
      shadow={selected ? 'md' : 'sm'}
      padding="md"
      radius="md"
      withBorder
      style={{
        cursor: 'pointer',
        borderColor: selected ? 'var(--mantine-color-blue-5)' : undefined,
        backgroundColor: selected ? 'var(--mantine-color-blue-0)' : undefined,
      }}
      onClick={onSelect}
    >
      <Group justify="space-between" mb="xs">
        <Group gap="sm">
          <ThemeIcon size="md" variant="light" color="blue">
            <IconUser size={16} />
          </ThemeIcon>
          <Text fw={600}>{item.subjectName}</Text>
        </Group>
        <StatusBadge status={item.status} />
      </Group>

      <Text size="sm" c="dimmed" mb="xs">
        {subjectLabel} ID: {item.subjectId}
      </Text>

      {item.activityDomains.length > 0 && (
        <Group gap="xs" mb="xs">
          {item.activityDomains.slice(0, 3).map((domain) => (
            <Badge key={domain} size="xs" variant="outline">
              {domain}
            </Badge>
          ))}
          {item.activityDomains.length > 3 && (
            <Badge size="xs" variant="outline" color="gray">
              +{item.activityDomains.length - 3}
            </Badge>
          )}
        </Group>
      )}

      <Divider my="xs" />

      <Group justify="space-between">
        <Group gap="xs">
          <IconClock size={14} />
          <Text size="xs" c="dimmed">
            Waiting {waitingDays} day{waitingDays !== 1 ? 's' : ''}
          </Text>
        </Group>
        {item.reviewedAt && (
          <Badge size="xs" variant="dot" color="green">
            Reviewed
          </Badge>
        )}
      </Group>
    </Card>
  );
}

// =============================================================================
// Review Panel with Dynamic Renderer
// =============================================================================

function ReviewPanel({
  item,
  rendererName,
  metadata,
}: {
  item: PlanQueueItem;
  rendererName?: string;
  metadata?: Record<string, unknown>;
}) {
  const DomainRenderer = useRenderer(rendererName);

  // If we have a domain-specific renderer, use it
  if (DomainRenderer) {
    return (
      <Suspense fallback={<Skeleton height={400} />}>
        <DomainRenderer entity={item as unknown as Record<string, unknown>} />
      </Suspense>
    );
  }

  // Generic fallback renderer
  return (
    <Stack gap="md">
      <Paper p="md" withBorder>
        <Group justify="space-between">
          <Group gap="md">
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconUser size={20} />
            </ThemeIcon>
            <div>
              <Text fw={600} size="lg">
                {item.subjectName}
              </Text>
              <Text size="sm" c="dimmed">
                ID: {item.subjectId}
              </Text>
            </div>
          </Group>
          <StatusBadge status={item.status} />
        </Group>
      </Paper>

      {item.reviewerNotes && (
        <Paper p="md" withBorder>
          <Text fw={500} size="sm" mb="sm">
            Reviewer Notes
          </Text>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {item.reviewerNotes}
          </Text>
        </Paper>
      )}

      <Paper p="md" withBorder>
        <Text fw={500} size="sm" mb="sm">
          Activity Domains
        </Text>
        <Group gap="xs">
          {item.activityDomains.map((domain) => (
            <Badge key={domain} variant="light">
              {domain}
            </Badge>
          ))}
        </Group>
      </Paper>
    </Stack>
  );
}

// =============================================================================
// Main Page
// =============================================================================

export function PlanApprovalQueuePage() {
  const { user, tenant } = useAuth();
  const viewLayer = useViewLayer();
  const { getLabel } = useResolvedTerminology();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Determine authority role from user roles
  // Backend will filter queue based on this role
  const authorityRole = user?.roles.includes('admin')
    ? 'approving_authority'
    : user?.roles.includes('architect')
    ? 'reviewer'
    : 'user';

  // Subject label from terminology (resident, client, account, project)
  const subjectLabel = getLabel('subject') || 'Subject';

  // Fetch queue
  const { data, loading, error, refetch } = useQuery<{
    approverPlanQueue: PlanQueueItem[];
  }>(APPROVER_PLAN_QUEUE, {
    variables: { authorityRole, limit: 50 },
    skip: !user,
    pollInterval: 30000,
  });

  // Get renderer name from tenant metadata
  const rendererName = tenant?.features?.['planQueueRenderer'] as string | undefined;

  const queueItems: PlanQueueItem[] = data?.approverPlanQueue ?? [];
  const selectedItem = queueItems.find((item) => item.planId === selectedId);

  // Access check
  if (!user) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Authentication Required" icon={<IconAlertTriangle />}>
          <Text>Please log in to access the plan approval queue.</Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Plan Approval Queue</Title>
            <Text c="dimmed" size="sm">
              Review and approve {subjectLabel.toLowerCase()} plans
            </Text>
          </div>
          <Button
            variant="subtle"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>

        {/* Error state */}
        {error && (
          <Alert color="red" title="Error Loading Queue">
            {error.message}
          </Alert>
        )}

        {/* Empty state */}
        {!loading && queueItems.length === 0 && (
          <Paper p="xl" withBorder ta="center">
            <ThemeIcon size="xl" variant="light" color="green" mx="auto" mb="md">
              <IconCheck size={24} />
            </ThemeIcon>
            <Title order={4}>All Caught Up</Title>
            <Text c="dimmed" size="sm">
              No plans awaiting your review
            </Text>
          </Paper>
        )}

        {/* Main content */}
        {queueItems.length > 0 && (
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {/* Queue list */}
            <Stack gap="sm">
              <Text fw={500} size="sm" c="dimmed">
                {queueItems.length} plan{queueItems.length !== 1 ? 's' : ''} pending
                review
              </Text>
              <LoadingOverlay visible={loading} />
              {queueItems.map((item) => (
                <QueueItemCard
                  key={item.planId}
                  item={item}
                  onSelect={() => setSelectedId(item.planId)}
                  selected={selectedId === item.planId}
                  subjectLabel={subjectLabel}
                />
              ))}
            </Stack>

            {/* Review panel */}
            <div>
              {selectedItem ? (
                <ReviewPanel
                  item={selectedItem}
                  rendererName={rendererName}
                />
              ) : (
                <Paper p="xl" withBorder ta="center" h="100%">
                  <Stack justify="center" h="100%" align="center">
                    <ThemeIcon size="xl" variant="light" color="gray">
                      <IconChevronRight size={24} />
                    </ThemeIcon>
                    <Text c="dimmed">Select a plan to review</Text>
                  </Stack>
                </Paper>
              )}
            </div>
          </SimpleGrid>
        )}
      </Stack>
    </Container>
  );
}

export default PlanApprovalQueuePage;
